import { z } from "zod";
const timestampSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const versionSchema = z.number().int().min(1).max(Number.MAX_SAFE_INTEGER);
const reasonSchema = z.string().trim().min(8).max(1_000);
const cursorSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/)
    .min(1)
    .max(256);
const boundedIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/);
const actionSchema = z.string().trim().min(1).max(100);
export const companionModeSchema = z.enum(["compliance", "education"]);
export const admissionModeSchema = z.enum(["open", "invite_only", "approved_domain"]);
export const constitutionStatusSchema = z.enum(["draft", "active", "retired"]);
export const approvedDomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(253)
    .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/);
export const approvedDomainsSchema = z
    .array(approvedDomainSchema)
    .max(50)
    .transform((domains) => [...new Set(domains)]);
export const adminCompanionSettingsSchema = z.strictObject({
    mode: companionModeSchema,
    complianceProgressEnabled: z.boolean(),
    complianceRecommendationsEnabled: z.boolean(),
    achievementsEnabled: z.boolean(),
    activeConstitution: z.strictObject({
        id: boundedIdSchema,
        version: z.string().min(1).max(128),
        contentHash: z.string().regex(/^[0-9a-f]{64}$/),
    }),
    version: versionSchema,
    updatedAt: timestampSchema,
});
export const adminCompanionSettingsUpdateInputSchema = z.strictObject({
    mode: companionModeSchema,
    complianceProgressEnabled: z.boolean(),
    complianceRecommendationsEnabled: z.boolean(),
    achievementsEnabled: z.boolean(),
    expectedVersion: versionSchema,
    reason: reasonSchema,
});
export const adminAdmissionSettingsSchema = z.strictObject({
    admissionMode: admissionModeSchema,
    approvedDomains: approvedDomainsSchema,
    activeTermsPolicyId: boundedIdSchema,
    activeGuardianPolicyId: boundedIdSchema,
    version: versionSchema,
    updatedAt: timestampSchema,
});
export const adminAdmissionSettingsUpdateInputSchema = z
    .strictObject({
    admissionMode: admissionModeSchema,
    approvedDomains: approvedDomainsSchema,
    expectedVersion: versionSchema,
    reason: reasonSchema,
})
    .superRefine((value, context) => {
    if (value.admissionMode === "approved_domain" && value.approvedDomains.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["approvedDomains"],
            message: "approved_domain requires at least one approved domain",
        });
    }
});
const boundedJsonLimits = {
    maxDepth: 6,
    maxFields: 128,
    maxArrayLength: 128,
    maxStringLength: 4_096,
    maxKeyLength: 128,
};
export const adminBoundedMetadataSchema = boundedJsonObjectSchema({ maxDepth: 4, maxFields: 64, maxArrayLength: 64, maxStringLength: 2_048, maxKeyLength: 128 }, 16_384, "invalid metadata");
export const adminConstitutionContentSchema = boundedJsonObjectSchema(boundedJsonLimits, 32_768, "invalid constitution content");
export const adminConstitutionItemSchema = z.strictObject({
    id: boundedIdSchema,
    version: z.string().trim().min(1).max(128),
    status: constitutionStatusSchema,
    content: adminConstitutionContentSchema,
    contentHash: z.string().regex(/^[0-9a-f]{64}$/),
    createdByUserId: z.string().min(1).max(128).nullable(),
    activationReason: z.string().min(1).max(1_000).nullable(),
    createdAt: timestampSchema,
    activatedAt: timestampSchema.nullable(),
    retiredAt: timestampSchema.nullable(),
});
export const adminConstitutionListResponseSchema = z.strictObject({
    settingsVersion: versionSchema,
    activeConstitutionId: boundedIdSchema,
    items: z.array(adminConstitutionItemSchema).max(100),
});
export const adminConstitutionCreateInputSchema = z.strictObject({
    version: z.string().trim().min(1).max(128),
    content: adminConstitutionContentSchema,
    reason: reasonSchema,
});
export const adminConstitutionCreateResponseSchema = z.strictObject({
    constitution: adminConstitutionItemSchema,
});
export const adminConstitutionActivateInputSchema = z.strictObject({
    expectedSettingsVersion: versionSchema,
    reason: reasonSchema,
});
export const adminConstitutionRetireInputSchema = z.strictObject({
    reason: reasonSchema,
});
export const adminConstitutionActionResponseSchema = z.strictObject({
    constitution: adminConstitutionItemSchema,
    settingsVersion: versionSchema,
});
export const adminAuditListQuerySchema = z.strictObject({
    action: actionSchema.optional(),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const adminAuditEventSchema = z.strictObject({
    id: boundedIdSchema,
    operationKey: z.string().min(1).max(256).nullable(),
    adminUserId: z.string().min(1).max(128).nullable(),
    adminEmail: z.email().nullable(),
    action: actionSchema,
    targetUserId: z.string().min(1).max(128).nullable(),
    targetOwnerId: z.string().min(1).max(128).nullable(),
    targetDeletedSubjectId: z.string().min(1).max(128).nullable(),
    targetAssetId: z.string().min(1).max(128).nullable(),
    reason: reasonSchema,
    metadata: adminBoundedMetadataSchema,
    createdAt: timestampSchema,
});
export const adminAuditListResponseSchema = z.strictObject({
    items: z.array(adminAuditEventSchema).max(50),
    nextCursor: cursorSchema.optional(),
});
export const adminStaleConflictResponseSchema = z.strictObject({
    error: z.strictObject({
        code: z.literal("stale_version"),
        message: z.string(),
        requestId: z.string(),
    }),
    current: z.union([adminCompanionSettingsSchema, adminAdmissionSettingsSchema]),
});
export const adminConstitutionStaleConflictResponseSchema = z.strictObject({
    error: z.strictObject({
        code: z.literal("stale_version"),
        message: z.string(),
        requestId: z.string(),
    }),
    current: adminConstitutionListResponseSchema,
});
function boundedJsonObjectSchema(limits, maxBytes, message) {
    return z
        .unknown()
        .superRefine((value, context) => {
        const error = jsonObjectError(value, limits);
        if (error !== undefined)
            context.addIssue({ code: "custom", message: `${message}: ${error}` });
        if (error === undefined && utf8Length(JSON.stringify(value)) > maxBytes) {
            context.addIssue({ code: "custom", message: `${message}: too large` });
        }
    })
        .transform((value) => value);
}
function jsonObjectError(value, limits) {
    if (!isPlainObject(value))
        return "must be a plain object";
    const state = { fields: 0, seen: new Set() };
    return jsonValueError(value, 0, limits, state);
}
function jsonValueError(value, depth, limits, state) {
    if (depth > limits.maxDepth)
        return "maximum depth exceeded";
    if (value === null || typeof value === "boolean")
        return undefined;
    if (typeof value === "number")
        return Number.isFinite(value) ? undefined : "number must be finite";
    if (typeof value === "string") {
        return value.length <= limits.maxStringLength ? undefined : "string too long";
    }
    if (typeof value !== "object")
        return "contains a non-JSON value";
    if (state.seen.has(value))
        return "contains a cycle";
    state.seen.add(value);
    if (Array.isArray(value)) {
        if (value.length > limits.maxArrayLength)
            return "array too long";
        for (const item of value) {
            const error = jsonValueError(item, depth + 1, limits, state);
            if (error !== undefined)
                return error;
        }
        return undefined;
    }
    if (!isPlainObject(value))
        return "contains a non-plain object";
    const entries = Object.entries(value);
    state.fields += entries.length;
    if (state.fields > limits.maxFields)
        return "too many fields";
    for (const [key, item] of entries) {
        if (key.length === 0 || key.length > limits.maxKeyLength)
            return "invalid field name";
        const error = jsonValueError(item, depth + 1, limits, state);
        if (error !== undefined)
            return error;
    }
    return undefined;
}
function isPlainObject(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function utf8Length(value) {
    let bytes = 0;
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0;
        bytes += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
    }
    return bytes;
}
//# sourceMappingURL=admin-settings.js.map